import { type GeoSpaceChiban } from "@/features/map/types";
import { type GeocodedPoint } from "@/features/map/types/geocodedPoint";
import {
  type SearchChainEntry,
  type SearchSourceId,
} from "@/features/map/utils/buildSearchChain";

/**
 * データソース 1 件を引いた結果。
 *
 * GEOSPACE / ゼンリンは hit か miss しか返さない。役所系 2 ソースは BE の envelope に
 * 合わせて、非提供エリア（not_available）、筆は外れたが住所の geocode には成功した
 * ケース（not_found + geocoded）、住所の geocode 自体に失敗したケース（failed）を
 * 区別して返す。登記所備付地図は BE のレート制限（429）に掛かったことも返す。
 */
export type SourceOutcome =
  | { kind: "hit"; result: GeoSpaceChiban }
  | { kind: "miss" }
  | { kind: "notAvailable"; message: string }
  | { kind: "geocoded"; geocoded: GeocodedPoint }
  | { kind: "geocodeFailed" }
  | { kind: "rateLimited" };

/** ソースがレート制限に掛かり、チェーンを打ち切ったときに利用者へ出す文言。 */
export const RATE_LIMITED_MESSAGE =
  "検索が集中しています。しばらく待ってから再度お試しください。";

/** チェーンの 1 ソースを引く関数。呼び出し側が入力（座標 / 地番 / 住所）を束縛して渡す。 */
export type SourceLookup = () => Promise<SourceOutcome>;

export interface RunSearchChainParams {
  /** `buildSearchChain` が返した優先順付きのソース列。 */
  chain: SearchChainEntry[];
  /**
   * ソースごとの検索関数。その検索を持たないソースは未定義にしておくと、
   * チェーンに載っていてもスキップされる。
   */
  lookup: Partial<Record<SearchSourceId, SourceLookup>>;
  /**
   * 住居表示を伴わないヒットを採用しないソースの集合。
   *
   * 登記地番検索のみで使う。GEOSPACE / ゼンリンは住居表示が返らないと結果として
   * 不十分なため後段へ流す一方、役所系は地番だけでも成立する、という既存の判定差を
   * 明示的に引き継ぐためのもの。
   */
  requiresAddress?: ReadonlySet<SearchSourceId>;
}

/**
 * チェーンを走らせた結果。地図移動・ポップアップ・トースト等の副作用は持たず、
 * 呼び出し側が kind に応じて適用する。
 */
export type ChainResolution =
  | {
      kind: "hit";
      result: GeoSpaceChiban;
      sourceId: SearchSourceId;
      /** 1 = 表示中のソースでヒット（乖離なし）、2 = 非表示のソースでヒット（要リカバリー）。 */
      tier: 1 | 2;
    }
  | {
      kind: "geocodedPin";
      geocoded: GeocodedPoint;
      sourceId: SearchSourceId;
    }
  | { kind: "notAvailable"; message: string; sourceId: SearchSourceId }
  | {
      kind: "notFound";
      /**
       * 役所系ソースが BE で入力住所の Google ジオコーディングに失敗していたか。
       * true のとき同じ住所で Google を呼び直しても結果は変わらないため、
       * 呼び出し側は Google フォールバック（whenSearchFail 等）を省く。
       */
      geocodeFailed?: true;
    }
  | {
      /**
       * ソースが BE のレート制限に掛かり、チェーンを打ち切った。
       * 後段のソース（有料のゼンリン等）や Google フォールバックは呼ばず、
       * 呼び出し側は時間をおいて再検索するよう案内する。
       */
      kind: "rateLimited";
      sourceId: SearchSourceId;
    };

/**
 * 検索チェーンを先頭から順に引き、最初のヒットで打ち切る。
 *
 * クリック・登記地番検索・住居表示検索が共通で使う。ソースを叩く条件はすべて
 * `buildSearchChain` 側で決まっているため、ここは「順に引いて最初に当たったものを返す」
 * だけに徹する。
 *
 * ソースがレート制限に掛かったら、その時点で打ち切って rateLimited を返す。
 * 制限は機械的な連打を止めるためのもので、後段の有料ソースへ流すと意味が無くなるため。
 *
 * どのソースも当たらなかった場合は、後段のフォールバックに使える情報を優先度順に返す:
 * 1. 役所系が返した geocode 済み座標（BE がジオコーディング済みなので、FE から Google を
 *    呼び直さずにピンを置ける）
 * 2. 表示中のソース（Tier 1）が返した非提供エリアの通知（他ソースも全滅した最終段でのみ出す）
 * 3. なければ notFound（役所系で住所のジオコーディングに失敗していればその旨を添える）
 *
 * @returns 解決結果。副作用は呼び出し側が kind に応じて適用する。
 */
export const runSearchChain = async ({
  chain,
  lookup,
  requiresAddress,
}: RunSearchChainParams): Promise<ChainResolution> => {
  let geocodedFallback:
    | { geocoded: GeocodedPoint; sourceId: SearchSourceId }
    | undefined;
  let notAvailable: { message: string; sourceId: SearchSourceId } | undefined;
  let geocodeFailed = false;

  for (const entry of chain) {
    const run = lookup[entry.id];
    if (!run) continue;

    const outcome = await run();

    if (outcome.kind === "rateLimited") {
      return { kind: "rateLimited", sourceId: entry.id };
    }

    if (outcome.kind === "hit") {
      // 住居表示を要するソースで住居表示が無いときは、採用せず後段へ流す
      if (requiresAddress?.has(entry.id) && !outcome.result.address) {
        continue;
      }
      return {
        kind: "hit",
        result: outcome.result,
        sourceId: entry.id,
        tier: entry.tier,
      };
    }

    if (outcome.kind === "geocodeFailed") {
      geocodeFailed = true;
      continue;
    }

    if (outcome.kind === "geocoded") {
      // 先に見つけたものを優先する（チェーンの優先順がそのまま座標の優先順になる）
      geocodedFallback ??= { geocoded: outcome.geocoded, sourceId: entry.id };
      continue;
    }

    // 即時表示せず、他ソースも全滅した最終段で出す（複数 ON 時のノイズ防止）。
    // 非表示のソース（Tier 2）の非提供通知は採用せず miss と同じに扱う。利用者が
    // 表示していないソースの対応範囲を告げても意味が無く、後段の Google
    // フォールバックまで潰してしまうため。
    if (outcome.kind === "notAvailable" && entry.tier === 1) {
      notAvailable ??= { message: outcome.message, sourceId: entry.id };
    }
  }

  if (geocodedFallback) {
    return { kind: "geocodedPin", ...geocodedFallback };
  }
  if (notAvailable) {
    return { kind: "notAvailable", ...notAvailable };
  }
  return geocodeFailed
    ? { kind: "notFound", geocodeFailed: true }
    : { kind: "notFound" };
};
